import { FilterGroupDefn, FilterDefn } from 'rwl-ui';
import { LabelSummaryWithUsage } from 'rwl-shared/dist/models/plugin/Plugin';
import {
    labelsToHierarchy,
    LabelHierarchy,
    LabelHierarchyDefn,
    ServiceChildrenLabelGroupNames,
} from 'rwl-shared/dist/utils/label';
import { Config } from './useConfig';

export const NON_TAG_PREFIX = 'Content:';
export const HAS_REVIEW = NON_TAG_PREFIX + 'reviews';
export const HAS_PHOTOS = NON_TAG_PREFIX + 'photo';

export const configToFilters = (config: Config): FilterGroupDefn[] => {
    const hasLabels = config?.account?.labelsWithUsage !== undefined;

    if (hasLabels) {
        return labelsToFilters(config.account.labelsWithUsage!);
    }

    return [];
};

const labelsToFilters = (
    labels: LabelSummaryWithUsage[]
): FilterGroupDefn[] => {
    const result: FilterGroupDefn[] = [];

    // remove labels with no usage
    const filteredLabels = labels.filter((label) => label.timesUsed > 0);

    // organize labels into a hierarchy
    const labelHierarchy = labelsToHierarchy(filteredLabels);

    // Service labels get treated a little different, split them out
    const { Service, ...otherLabels } = labelHierarchy;

    // Build Service label filter (i.e. Roofing) and nested filters (i.e. Roofing -> Service Type -> Roof Repair)
    const serviceLabelFilters = buildServicesFilterGroup(Service);
    result.push(...serviceLabelFilters);

    // Bring the categories above the services, group categories by name
    const categories = moveCategoryAboveService(Service);

    // Pull out the Job Type category, it gets special treatment
    const { ['Job Type']: JobType } = categories;

    // Process the Job Type labels
    const jobTypeFilter = jobTypeLabelsToFilterGroup(JobType);
    if (jobTypeFilter) {
        result.push(jobTypeFilter);
    }

    // Process the remaining (non-service) labels - i.e. Customer Type
    const otherFilters = Object.values(otherLabels || {}).map(
        labelHierarchyToFilterGroup
    );
    result.push(...otherFilters);

    // Add the special case filters for content exists
    const contentFilter = addContentFilters();
    result.push(contentFilter);

    return result;
};

const labelHierarchyToFilterGroup = (label: LabelHierarchyDefn) => {
    const filterGroup: FilterGroupDefn = {
        key: label.externalId,
        title: label.name,
        type: 'multiList',
        options: [],
    };

    if (label.children) {
        filterGroup.options = Object.values(label.children).map(
            (l: LabelHierarchyDefn) => ({
                title: l.name,
                value: l.externalId,
            })
        );
    }

    return filterGroup;
};

const jobTypeLabelsToFilterGroup = (label: LabelHierarchyDefn) => {
    if (!label?.children) {
        return undefined;
    }

    const filterGroup: FilterGroupDefn = {
        key: 'JobType',
        title: 'Job Type',
        type: 'multiList',
        options: [],
    };

    // deduplicate all the job types by name
    const services = Object.values(label.children || {});
    const jobTypes = services.reduce((acc, service) => {
        Object.values(service.children || {}).forEach(
            (jobType: LabelHierarchyDefn) => {
                acc[jobType.name] = {
                    title: jobType.name,
                    value: '',
                    valueList: [
                        ...(acc[jobType.name]?.valueList || []),
                        jobType.externalId,
                    ],
                };
            }
        );
        return acc;
    }, {} as Record<string, FilterDefn>);

    filterGroup.options = Object.values(jobTypes);

    return filterGroup;
};

function moveCategoryAboveService(Service: LabelHierarchyDefn): LabelHierarchy {
    const services = Object.values(Service?.children || {});
    const categories = services.reduce(
        (acc: LabelHierarchy, service: LabelHierarchyDefn) => {
            const categories = Object.values(service.children || {});
            if (categories.length) {
                categories.forEach(
                    ({
                        externalId,
                        name,
                        children = {},
                    }: LabelHierarchyDefn) => {
                        acc[name] = {
                            externalId,
                            name,
                            children: {
                                ...(acc[name]?.children || {}),
                                [service.externalId]: {
                                    externalId: service.externalId,
                                    name: service.name,
                                    children,
                                },
                            },
                        };
                    }
                );
            }
            return acc;
        },
        {}
    );
    return categories;
}

const addContentFilters = (): FilterGroupDefn => {
    return {
        key: 'content',
        title: 'Content',
        type: 'multiList',
        options: [
            {
                title: 'Must have reviews',
                value: HAS_REVIEW,
            },
            {
                title: 'Must have photos',
                value: HAS_PHOTOS,
            },
        ],
    };
};

export const buildServicesFilterGroup = (
    Service: LabelHierarchyDefn
): FilterGroupDefn[] => {
    const services = Object.values(Service?.children || {})
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((serviceLabel) => {
            return serviceLabel?.externalId !== 'Service:Unknown';
        });
    const servicesOptions = services.map((service: LabelHierarchyDefn) => {
        const servicesLabels: FilterDefn = {
            title: service.name,
            value: service.externalId,
            subFilters: [],
        };
        servicesLabels.subFilters = Object.values(service?.children || {})
            .filter(
                (tagGroup: LabelHierarchyDefn) =>
                    // filter out Job Type and any tag groups with no children (since tag groups are not tags themselves and cannot be selected)
                    Object.keys(tagGroup?.children || {}).length > 0 &&
                    tagGroup.name !== ServiceChildrenLabelGroupNames.jobType
            )
            .sort((a, b) => {
                // bring Service Type to the top
                if (a.name === ServiceChildrenLabelGroupNames.serviceType) {
                    return -1;
                }
                if (b.name === ServiceChildrenLabelGroupNames.serviceType) {
                    return 1;
                }
                return a.name.localeCompare(b.name);
            })
            .map((tagGroup: LabelHierarchyDefn) => {
                const tagGroupFilter: FilterDefn = {
                    title: tagGroup.name,
                    value: tagGroup.externalId,
                    // this layer (tag group) is always display only
                    displayOnly: true,
                    subFilters: [],
                };
                tagGroupFilter.subFilters = Object.values(
                    tagGroup?.children || {}
                )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((tagGroupChild) => {
                        return {
                            title: tagGroupChild.name,
                            value: tagGroupChild.externalId,
                        };
                    });
                return tagGroupFilter;
            });
        return servicesLabels;
    });
    const servicesFilterGroup: FilterGroupDefn = {
        key: 'serviceLabels',
        title: 'Services',
        type: 'nestedMultiList',
        options: [...servicesOptions],
    };
    return [servicesFilterGroup];
};
